<script setup lang="ts">
import CorporationInfo from './CorporationInfo.vue';
import RouteInfo from './RouteInfo.vue';
import TimeInfo from './TimeInfo.vue';
import type TripSegment from '@itinerary/trip/TripSegment';
import { VehicleType } from '@itinerary/trip/types';

interface IProps {
    segment: TripSegment;
    vehicleType: VehicleType;
    international?: boolean;
    inAutoReserve?: boolean;
    mobileView?: boolean;
}
const { segment, mobileView, international, vehicleType, inAutoReserve } =
    defineProps<IProps>();
const showDuration = computed(
    () =>
        (vehicleType === VehicleType.Plane && international) ||
        vehicleType === VehicleType.Train
);
const destinationHasWarning = computed(
    () => segment.firstTrip.destinationHasWarning
);
</script>

<template>
    <div
        class="segment-view-container"
        :class="{ 'in-auto-reserve': inAutoReserve }"
    >
        <CorporationInfo class="co-info" :segment :mobile-view />
        <section
            class="route-section"
            :class="{
                'mobile-view': mobileView,
                'auto-reserve': inAutoReserve,
            }"
        >
            <div class="route">
                <TimeInfo
                    :time="segment.departureTime"
                    :location="segment.origin"
                    :hide-location="inAutoReserve"
                />
                <RouteInfo :segment :show-duration full-width />
                <TimeInfo
                    :time="segment.arrivalTime"
                    :location="segment.destination"
                    destination
                    left-sided
                    :hide-location="inAutoReserve"
                    :duration-days="segment.durationDays"
                    :destination-has-warning
                />
            </div>
            <div class="locations">
                <mr-typography
                    :size="{
                        mobile: 'small',
                        desktop: 'x-large',
                    }"
                    color="var(--cool-gray-700)"
                    weight="500"
                >
                    {{ segment.origin }}
                </mr-typography>
                <mr-typography
                    :size="{
                        mobile: 'small',
                        desktop: 'x-large',
                    }"
                    color="var(--cool-gray-700)"
                    weight="500"
                >
                    {{ segment.destination }}
                </mr-typography>
            </div>
        </section>
        <div class="desktop-only">
            <slot name="extra-detail" />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.segment-view-container {
    display: flex;
    align-items: center;
    order: 1;
    gap: 12px;
    @include desktop-only {
        gap: 24px;
    }
    &.in-auto-reserve {
        .co-info {
            padding: 0;
        }
        .route-section {
            padding: 0;
        }
    }
}

.route-section {
    flex: 1;
    display: flex;
    align-items: flex-end;
    padding: 0;
    padding-top: 12px;
    flex-direction: column;
    .route {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
    }
    .locations {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
    }
    :deep(.route-view-container) {
        display: flex;
        align-items: center;
        height: 100%;
    }
    &.mobile-view {
        flex: 1;
        padding: 0;
    }
    @include desktop-only {
        max-width: 300px;
    }
}
</style>
